import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../theme';
import { useIsMobile } from '../useMediaQuery';
import ProGate from './ProGate';

const FLAG = { BR: '\u{1F1E7}\u{1F1F7}', US: '\u{1F1FA}\u{1F1F8}', EU: '\u{1F1EA}\u{1F1FA}' };
const RISK_SCORE = { Sovereign: 1, 'Sovereign (FGC)': 1.5, 'Sovereign (AAA)': 1, Bank: 2, 'Low (fund)': 2, 'Smart Contract': 4, 'DeFi Pool': 5 };

function riskLabel(r) {
  const s = RISK_SCORE[r] || 3;
  if (s <= 1.5) return { text: 'Very Low', color: '#0D7A4E' };
  if (s <= 2) return { text: 'Low', color: '#0D7A4E' };
  if (s <= 3) return { text: 'Medium', color: '#8A6A00' };
  return { text: 'High', color: '#B8001A' };
}

export default function FixedIncomeCompareView({ pools }) {
  const { colors, dark } = useTheme();
  const isMobile = useIsMobile();
  const [tradfiRates, setTradfiRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [investment, setInvestment] = useState(10000);
  const [months, setMonths] = useState(12);
  const [taxRate, setTaxRate] = useState(15);
  const [region, setRegion] = useState('all');

  useEffect(() => {
    fetch('/api/rates/tradfi')
      .then(r => {
        if (!r.ok) throw new Error('API unavailable');
        return r.json();
      })
      .then(data => {
        setTradfiRates(data);
        setLoading(false);
      })
      .catch(() => {
        // Fallback rates when API is unavailable (dev mode / offline)
        setTradfiRates({
          brazil: [
            { instrument: 'Selic', rate: 14.75, country: 'BR', risk: 'Sovereign', liquidity: 'Daily', tax: 'IOF + IR regressivo (22.5%→15%)', source: 'Fallback' },
            { instrument: 'CDI', rate: 14.65, country: 'BR', risk: 'Bank', liquidity: 'Daily', tax: 'IR regressivo (22.5%→15%)', source: 'Fallback' },
            { instrument: 'Poupança', rate: 6.17, country: 'BR', risk: 'Sovereign (FGC)', liquidity: 'Monthly', tax: 'Isento para PF', source: 'Fallback' },
          ],
          us: [
            { instrument: 'Fed Funds Rate', rate: 4.33, country: 'US', risk: 'Sovereign', liquidity: 'Overnight', tax: 'US federal + state income tax', source: 'Fallback' },
            { instrument: '3-Month T-Bill', rate: 4.22, country: 'US', risk: 'Sovereign', liquidity: '3 months', tax: 'Federal income tax (state exempt)', source: 'Fallback' },
            { instrument: '10-Year T-Note', rate: 4.05, country: 'US', risk: 'Sovereign', liquidity: '10 years', tax: 'Federal income tax (state exempt)', source: 'Fallback' },
          ],
          europe: [
            { instrument: 'ECB Deposit Rate', rate: 2.50, country: 'EU', risk: 'Sovereign', liquidity: 'Overnight', tax: 'Varies by EU country', source: 'Fallback' },
            { instrument: 'EU Money Market', rate: 2.40, country: 'EU', risk: 'Low (fund)', liquidity: 'Daily', tax: 'Varies by country', source: 'Fallback' },
          ],
          updatedAt: new Date().toISOString(),
        });
        setLoading(false);
      });
  }, []);

  // Top DeFi pools for comparison
  const topPools = useMemo(() => {
    if (!pools?.length) return [];
    return pools
      .filter(p => p.stablecoin && p.risk === 'LOW')
      .sort((a, b) => b.apr - a.apr)
      .slice(0, 5)
      .map(p => ({
        instrument: `${p.name} (${p.protocol})`,
        rate: p.apr,
        country: 'DeFi',
        risk: 'Smart Contract',
        liquidity: 'Instant',
        tax: 'Varies',
        source: p.protocol,
        pool: p,
      }));
  }, [pools]);

  const allRates = useMemo(() => {
    if (!tradfiRates) return topPools;
    let rates = [];
    if (region === 'all' || region === 'BR') rates = [...rates, ...tradfiRates.brazil];
    if (region === 'all' || region === 'US') rates = [...rates, ...tradfiRates.us];
    if (region === 'all' || region === 'EU') rates = [...rates, ...tradfiRates.europe];
    return [...rates, ...topPools].filter(r => r.rate != null);
  }, [tradfiRates, topPools, region]);

  const calculateReturn = (rate, principal, months, taxPct) => {
    const gross = principal * (rate / 100) * (months / 12);
    const net = gross * (1 - taxPct / 100);
    return { gross, net, total: principal + net };
  };

  const content = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ maxWidth: 1000, margin: '0 auto', padding: isMobile ? '16px 12px' : '24px' }}
    >
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: isMobile ? 24 : 28,
          color: colors.textPrimary,
          marginBottom: 6,
        }}>
          DeFi vs Fixed Income
        </div>
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 12,
          color: colors.textSecondary,
        }}>
          Compare DeFi pool yields with traditional fixed income instruments
        </div>
      </div>

      {/* Calculator controls */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
        gap: 12,
        marginBottom: 20,
      }}>
        {/* Investment amount */}
        <div style={{
          background: colors.surface,
          borderRadius: 10,
          border: `1px solid ${colors.border}`,
          padding: 14,
        }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: colors.labelLight,
            letterSpacing: 1,
            marginBottom: 6,
          }}>INVESTMENT</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 16, color: colors.textPrimary }}>$</span>
            <input
              type="number"
              value={investment}
              onChange={e => setInvestment(Number(e.target.value) || 0)}
              style={{
                fontFamily: "'DM Serif Display', serif",
                fontSize: 20,
                color: colors.textPrimary,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                width: '100%',
              }}
            />
          </div>
        </div>

        {/* Months */}
        <div style={{
          background: colors.surface,
          borderRadius: 10,
          border: `1px solid ${colors.border}`,
          padding: 14,
        }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: colors.labelLight,
            letterSpacing: 1,
            marginBottom: 6,
          }}>PERIOD</div>
          <select
            value={months}
            onChange={e => setMonths(Number(e.target.value))}
            style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 20,
              color: colors.textPrimary,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            {[1, 3, 6, 12, 24, 36].map(m => (
              <option key={m} value={m}>{m} mo</option>
            ))}
          </select>
        </div>

        {/* Tax Rate */}
        <div style={{
          background: colors.surface,
          borderRadius: 10,
          border: `1px solid ${colors.border}`,
          padding: 14,
        }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: colors.labelLight,
            letterSpacing: 1,
            marginBottom: 6,
          }}>TAX RATE</div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input
              type="range"
              min="0"
              max="45"
              value={taxRate}
              onChange={e => setTaxRate(Number(e.target.value))}
              style={{ width: '70%' }}
            />
            <span style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 18,
              color: colors.textPrimary,
              marginLeft: 8,
            }}>{taxRate}%</span>
          </div>
        </div>

        {/* Region */}
        <div style={{
          background: colors.surface,
          borderRadius: 10,
          border: `1px solid ${colors.border}`,
          padding: 14,
        }}>
          <div style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 9,
            color: colors.labelLight,
            letterSpacing: 1,
            marginBottom: 6,
          }}>REGION</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {['all', 'BR', 'US', 'EU'].map(r => (
              <button
                key={r}
                onClick={() => setRegion(r)}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  fontWeight: region === r ? 600 : 400,
                  color: region === r ? colors.green : colors.textSecondary,
                  background: region === r ? colors.greenBg : 'transparent',
                  border: `1px solid ${region === r ? colors.greenBorder : 'transparent'}`,
                  borderRadius: 4,
                  padding: '3px 8px',
                  cursor: 'pointer',
                }}
              >
                {r === 'all' ? 'All' : `${FLAG[r] || ''} ${r}`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Comparison table */}
      <div style={{
        background: colors.surface,
        borderRadius: 12,
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{
            padding: 40,
            textAlign: 'center',
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 12,
            color: colors.labelMuted,
          }}>
            Loading rates from BCB, FRED, ECB...
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: dark ? 'rgba(255,255,255,0.03)' : '#FAFAFA' }}>
                  {['INSTRUMENT', 'RATE', 'RISK', 'LIQUIDITY', 'GROSS RETURN', 'NET RETURN'].map(h => (
                    <th key={h} style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 9,
                      fontWeight: 600,
                      color: colors.labelLight,
                      letterSpacing: 1.2,
                      textAlign: 'left',
                      padding: '10px 14px',
                      borderBottom: `1px solid ${colors.border}`,
                      whiteSpace: 'nowrap',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allRates
                  .sort((a, b) => (b.rate || 0) - (a.rate || 0))
                  .map((r, i) => {
                    const ret = calculateReturn(r.rate, investment, months, taxRate);
                    const rl = riskLabel(r.risk);
                    const isDeFi = r.country === 'DeFi';
                    return (
                      <tr key={i} style={{
                        borderBottom: `1px solid ${dark ? 'rgba(255,255,255,0.04)' : '#F4F5F6'}`,
                        background: isDeFi
                          ? (dark ? 'rgba(13,122,78,0.05)' : 'rgba(13,122,78,0.02)')
                          : 'transparent',
                      }}>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 12,
                            color: colors.textPrimary,
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}>
                            <span style={{ fontSize: 14 }}>{FLAG[r.country] || (isDeFi ? '\u{1F4B0}' : '')}</span>
                            {r.instrument}
                          </div>
                          <div style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 9,
                            color: colors.labelMuted,
                            marginTop: 2,
                          }}>
                            {r.source} {r.tax && `\u00B7 ${r.tax}`}
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            fontFamily: "'DM Serif Display', serif",
                            fontSize: 18,
                            color: (r.rate || 0) > 20 ? '#8A6A00' : colors.green,
                          }}>
                            {r.rate != null ? `${r.rate.toFixed(2)}%` : '—'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 10,
                            fontWeight: 600,
                            color: rl.color,
                            background: rl.color === '#0D7A4E' ? colors.greenBg : rl.color === '#8A6A00' ? '#FFF8E0' : '#FFF0F2',
                            border: `1px solid ${rl.color === '#0D7A4E' ? colors.greenBorder : rl.color === '#8A6A00' ? '#E0CC70' : '#F0A0A8'}`,
                            padding: '2px 8px',
                            borderRadius: 3,
                          }}>
                            {rl.text}
                          </span>
                        </td>
                        <td style={{
                          padding: '12px 14px',
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 11,
                          color: colors.textSecondary,
                        }}>
                          {r.liquidity}
                        </td>
                        <td style={{
                          padding: '12px 14px',
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: 12,
                          color: colors.textPrimary,
                        }}>
                          ${ret.gross.toFixed(2)}
                        </td>
                        <td style={{
                          padding: '12px 14px',
                          fontFamily: "'DM Serif Display', serif",
                          fontSize: 15,
                          color: ret.net > 0 ? colors.green : '#B8001A',
                          fontWeight: 500,
                        }}>
                          ${ret.net.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary */}
      {!loading && allRates.length > 0 && (
        <div style={{
          marginTop: 16,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10,
          color: colors.labelMuted,
          textAlign: 'center',
        }}>
          Rates updated: {tradfiRates?.updatedAt ? new Date(tradfiRates.updatedAt).toLocaleString() : 'N/A'}
          {' \u00B7 '}DeFi rates from DeFi Llama{' \u00B7 '}Calculations are estimates only
        </div>
      )}
    </motion.div>
  );

  return (
    <ProGate feature="DeFi vs Fixed Income comparison">
      {content}
    </ProGate>
  );
}
